// ./src/components/features/Onboarding/onboardingSteps.ts
//
// ROADMAP #161 — ACCOUNT onboarding, right after SIGN-UP (owner, 2026-10-16: "initially the idea was to setup a venue for
// new user during onboarding process right after account (not event) creation in case when they chose that they are
// venue that hosts events. Similarly it was with Stripe … And same with email confirmation"). Kept pure, so
// `npm run check-account-onboarding` RUNS the rules.
//
//   organization  the OWNER — the company and what it organizes, moved here from the sign-up form (owner, 2026-11-02). First,
//              and REQUIRED where the brand asks for a company (`onboardingGateTarget`): the one step with no skip.
//   email      everyone with an address — shown done once confirmed (ms_auth `emailConfirmation`).
//   venue      the OWNER of a VENUE account (`account_type` 'venue') — its own space, saved as the default venue
//              (`account_preference.default_venue_id`), so every new event starts there.
//   defaults   the OWNER — the account's event defaults (currency, how events are usually hosted, time zone, theme,
//              guest mode, the at-risk thresholds): the SAME `EventDefaultsForm` as Settings → Event defaults. Done once
//              the account has saved its defaults (a row exists — the venue step's save makes one too).
//   payments   the OWNER — Stripe, connected right here (`StripeConnectPanel`, the same as Money → Get paid); ms_auth
//              lets only the owner's role connect it (`can_manage_billing`, migration 370).
//
// ⚠ Every step is DERIVED from what is true now, never a stored "done" flag that drifts; each one is skippable. Home's
//   Getting started card links its "Confirm your email" step back here (`?step=email`); its venue and payout steps open
//   their own pages (Event defaults, Stripe), because an admin sees those steps too and is offered neither here.
// ⚠ An account type not READ yet is not "not a venue": the step list waits for it (`null`) rather than appear without the
//   venue step and grow one under the host's thumb.

import { VENUE_ACCOUNT_TYPE } from '@/config/accountTypes';

export const ONBOARDING_STEP = {
  ORGANIZATION: 'organization',
  EMAIL: 'email',
  // #198 b + c — the account's public identity: its organizer page, with its logo.
  ORGANIZER: 'organizer',
  VENUE: 'venue',
  DEFAULTS: 'defaults',
  PAYMENTS: 'payments',
  // #198 c — invite the people who run events with you.
  TEAM: 'team',
} as const;

export type OnboardingStepKey = (typeof ONBOARDING_STEP)[keyof typeof ONBOARDING_STEP];

/** The page, and the `?step=` that reopens one step of it (Home's Getting started links straight to a step). */
export const ONBOARDING_PATH = '/onboarding';

/**
 * The page at one step. #198 a — with `returnTo` (a path on this site) it is a DETOUR: only that step is shown, and the
 * page goes back there once it is done or left (the dashboard's "Confirm your email to publish").
 */
export const onboardingStepPath = (step: OnboardingStepKey, returnTo?: string | null): string =>
  `${ONBOARDING_PATH}?step=${step}${returnTo ? `&returnUrl=${encodeURIComponent(returnTo)}` : ''}`;

export interface OnboardingFacts {
  /** The signed-in user owns the account (Stripe and the account's defaults are the owner's). */
  isOwner: boolean;
  /** The user has an email address to confirm. */
  hasEmail: boolean;
  /** `account.account_type`; `undefined` = not read yet (or the read failed), `null` = none chosen. */
  accountType: string | null | undefined;
  /**
   * #198 c — the plan lets the account have more than one user (`MAX_USERS_QTY`): the team step is offered only then — on
   * a one-seat plan it could show nothing but an upsell.
   */
  teamAllowed: boolean;
}

/**
 * The steps this user is offered, in order. `null` while a fact that decides the LIST is still unknown — an owner's
 * account type — so the page shows a skeleton instead of a list that changes shape a moment later.
 * ⚠ A non-owner never waits on the account type: no step of theirs depends on it.
 */
export const onboardingSteps = (facts: OnboardingFacts): OnboardingStepKey[] | null => {
  if (facts.isOwner && facts.accountType === undefined) {
    return null;
  }

  const steps: OnboardingStepKey[] = [];

  if (facts.isOwner) {
    steps.push(ONBOARDING_STEP.ORGANIZATION);
  }

  if (facts.hasEmail) {
    steps.push(ONBOARDING_STEP.EMAIL);
  }

  // #198 b — EVERY account type (owner, 2026-10-21: "all the types"): the public identity its events are shown under.
  if (facts.isOwner) {
    steps.push(ONBOARDING_STEP.ORGANIZER);
  }

  if (facts.isOwner && facts.accountType === VENUE_ACCOUNT_TYPE) {
    steps.push(ONBOARDING_STEP.VENUE);
  }

  if (facts.isOwner) {
    steps.push(ONBOARDING_STEP.DEFAULTS, ONBOARDING_STEP.PAYMENTS);
  }

  if (facts.isOwner && facts.teamAllowed) {
    steps.push(ONBOARDING_STEP.TEAM);
  }

  return steps;
};

/**
 * #198 a — a DETOUR (the URL carries a `returnUrl`): just the step asked for, when this user is offered it — the host
 * came to confirm their email and goes straight back. Otherwise the full list.
 */
export const onboardingDetourSteps = (
  steps: OnboardingStepKey[] | null,
  requested: string | null | undefined,
  isDetour: boolean,
): OnboardingStepKey[] | null => {
  if (!steps || !isDetour) {
    return steps;
  }

  const asked = steps.find((step) => step === requested);

  return asked ? [asked] : steps;
};

/** Per step: done (`true`), not done (`false`), or not known yet (`null` — Stripe has not answered, say). */
export type OnboardingDone = Partial<Record<OnboardingStepKey, boolean | null>>;

/** A name that was read and is empty — what a new account has until it is named. Not read yet (`undefined`) is not missing. */
export const accountNameMissing = (accountName: string | null | undefined): boolean => typeof accountName === 'string' && !accountName.trim();

/**
 * Must this owner name their organization before anything else? Only an account NOT NAMED yet, where the brand asks for a
 * company. ⚠ So an account from before this step (named at sign-up, perhaps with no type) is never stopped. ⚠ A brand rule that
 * could not be read does not stop anyone: this is the door to the whole dashboard.
 */
export const organizationRequired = (facts: { isOwner: boolean; accountName: string | null | undefined; requiresCompany: boolean | null | undefined }): boolean =>
  facts.isOwner && facts.requiresCompany === true && accountNameMissing(facts.accountName);

/** Where the guard sends someone who must name their organization first, or null. Never from the onboarding, or sign-in and sign-out. */
export const onboardingGateTarget = (facts: { isOwner: boolean; accountName: string | null | undefined; requiresCompany: boolean | null | undefined; pathname: string }): string | null => {
  if (!organizationRequired(facts) || facts.pathname === ONBOARDING_PATH || facts.pathname.startsWith('/account/')) {
    return null;
  }

  return onboardingStepPath(ONBOARDING_STEP.ORGANIZATION);
};

export interface OnboardingDoneFacts {
  /** The account's name and type (undefined = not read), and whether the brand asks for a company (undefined / null = not known). */
  accountName: string | null | undefined;
  accountType: string | null | undefined;
  requiresCompany: boolean | null | undefined;
  /** The session user's `emailConfirmation`. */
  emailConfirmed: boolean | null | undefined;
  /** The account preferences: read (`isLoaded`), the read FAILED (`loadFailed`), and the saved default venue. */
  preferenceLoaded: boolean;
  preferenceLoadFailed: boolean;
  defaultVenueId: string | null | undefined;
  /** The account has a saved preferences row (an account that never saved reads an empty one, with no id). */
  preferenceSaved: boolean;
  /** Stripe: the check answered (`checked`), and its answer — null when the check FAILED. */
  paymentsChecked: boolean;
  chargesEnabled: boolean | null | undefined;
  paymentsAnswered: boolean;
  /**
   * #198 b — the account's organizer pages (their logo ids), or null while unread / when the read FAILED. Done = one of
   * them has a logo: the page AND the logo the step asks for.
   */
  organizerPages: Array<{ logoUploadedFileId?: string | null }> | null;
  /** #198 c — the account's members and open invitations, or null while unread / failed. Done = anyone besides the owner. */
  team: { members: number; invitations: number } | null;
}

/**
 * Each step's state. ⚠ A read that has not answered — or FAILED — is `null`, never `false`: a failed preferences read
 * is not "no default venue" (the step would offer an empty picker over one that exists), and a failed Stripe check is
 * not "not connected".
 */
export const onboardingDone = (facts: OnboardingDoneFacts): OnboardingDone => ({
  [ONBOARDING_STEP.ORGANIZATION]:
    facts.accountName === undefined || facts.requiresCompany == null
      ? null
      : !accountNameMissing(facts.accountName) && !!facts.accountName && (!facts.requiresCompany || !!facts.accountType),
  [ONBOARDING_STEP.EMAIL]: facts.emailConfirmed === true,
  [ONBOARDING_STEP.VENUE]: facts.preferenceLoaded && !facts.preferenceLoadFailed ? !!facts.defaultVenueId : null,
  [ONBOARDING_STEP.DEFAULTS]: facts.preferenceLoaded && !facts.preferenceLoadFailed ? facts.preferenceSaved : null,
  [ONBOARDING_STEP.PAYMENTS]: facts.paymentsChecked && facts.paymentsAnswered ? facts.chargesEnabled === true : null,
  [ONBOARDING_STEP.ORGANIZER]: facts.organizerPages ? facts.organizerPages.some((page) => !!page.logoUploadedFileId) : null,
  [ONBOARDING_STEP.TEAM]: facts.team ? facts.team.members > 1 || facts.team.invitations > 0 : null,
});

/**
 * The step to open: the one the URL asks for when this user is offered it, else the first that is not KNOWN to be done,
 * else the first. ⚠ Unknown counts as not done — opening past a step whose state could not be read would skip it
 * silently. Null only when there are no steps at all.
 */
export const resolveOnboardingStep = (
  requested: string | null | undefined,
  steps: readonly OnboardingStepKey[],
  done: OnboardingDone,
): OnboardingStepKey | null => {
  const asked = steps.find((step) => step === requested);

  if (asked) {
    return asked;
  }

  return steps.find((step) => done[step] !== true) ?? steps[0] ?? null;
};

/** The step after `current`, or null when `current` is the last (the page then finishes). */
export const nextOnboardingStep = (steps: readonly OnboardingStepKey[], current: OnboardingStepKey): OnboardingStepKey | null => {
  const at = steps.indexOf(current);

  return at >= 0 && at + 1 < steps.length ? steps[at + 1] : null;
};

/**
 * #198 c (review) — the invitations still OPEN at `now`: an expired one is nobody coming (ms_auth keeps it; an accepted one
 * is gone), so it does not make the team step done. An unreadable or missing expiry counts as open — the server decides.
 */
export const openInvitations = <T extends { expiresAt?: string | null }>(invitations: readonly T[], now: number): T[] =>
  invitations.filter((invitation) => {
    const until = invitation.expiresAt ? Date.parse(invitation.expiresAt) : NaN;

    return !Number.isFinite(until) || until > now;
  });
